// Decorative lightning bolt SVGs — purely visual, no interactivity.

const Bolt = ({ className = "", color = "#1E3FD8", strokeWidth = 1.5, fill = "none" }) => (
  <svg
    viewBox="0 0 24 32"
    className={className}
    fill={fill}
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 1 L3 18 L11 18 L9 31 L21 12 L13 12 Z" />
  </svg>
);

const SolidBolt = ({ className = "", color = "#F5C518" }) => (
  <svg viewBox="0 0 24 32" className={className} fill={color} aria-hidden="true">
    <path d="M14 1 L3 18 L11 18 L9 31 L21 12 L13 12 Z" />
  </svg>
);

/**
 * HeroBolts — scattered lightning bolts for the hero section background.
 */
export function HeroBolts() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <Bolt className="absolute top-[8%] left-[6%] w-8 h-10 opacity-60 animate-flicker" color="#F5C518" strokeWidth={1.4} />
      <Bolt className="absolute top-[18%] right-[12%] w-14 h-20 opacity-40 rotate-12" color="#1E3FD8" strokeWidth={1.4} />
      <SolidBolt className="absolute top-[35%] left-[4%] w-6 h-8 opacity-70 animate-pulse-glow rotate-[-8deg]" />
      <Bolt className="absolute bottom-[14%] right-[6%] w-20 h-24 opacity-30 rotate-[-15deg]" color="#F5C518" strokeWidth={1.4} />
      <Bolt className="absolute bottom-[28%] left-[42%] w-6 h-8 opacity-50 rotate-[20deg] animate-flicker" color="#1E3FD8" />
      <SolidBolt className="absolute top-[58%] right-[40%] w-4 h-5 opacity-80 rotate-[35deg]" color="#1E3FD8" />
      <Bolt className="absolute top-[72%] left-[10%] w-12 h-14 opacity-30 rotate-[-5deg]" color="#F5C518" />
      <SolidBolt className="absolute top-[12%] right-[28%] w-3 h-4 opacity-90 animate-flicker" />
      <SolidBolt className="absolute bottom-[40%] left-[26%] w-2 h-3 opacity-100" color="#F5C518" />
    </div>
  );
}

/**
 * SectionDecor — richer decoration for content sections.
 * Blobs give ambient color, bolts give brand accent, circuit lines add texture.
 * variant: "blue" | "yellow" | "mixed"
 */
export function SectionDecor({ variant = "mixed", corners = "both" }) {
  const showTL = corners === "both" || corners === "tl";
  const showTR = corners === "both" || corners === "tr";
  const showBL = corners === "both" || corners === "bl";
  const showBR = corners === "both" || corners === "br";

  // Note: we no longer paint big color blobs here — the page-wide ambient in
  // body::before handles the color atmosphere so sections blend seamlessly.
  // Only local accents (circuit texture + bolts) live in each section.

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {/* Circuit lines */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.08]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id={`circuit-${variant}-${corners}`} width="80" height="80" patternUnits="userSpaceOnUse">
            <path d="M 0 40 L 30 40 L 40 30 L 40 0 M 40 80 L 40 50 L 50 40 L 80 40" stroke="#1E3FD8" strokeWidth="1" fill="none" />
            <circle cx="40" cy="40" r="2" fill="#F5C518" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#circuit-${variant}-${corners})`} />
      </svg>

      {/* Bolts */}
      {showTL && <Bolt className="absolute top-8 left-4 w-14 h-18 opacity-25 rotate-[-10deg]" color="#1E3FD8" strokeWidth={1.6} />}
      {showTR && <Bolt className="absolute top-12 right-6 w-10 h-14 opacity-30 rotate-[12deg]" color="#F5C518" strokeWidth={1.6} />}
      {showBL && <Bolt className="absolute bottom-10 left-8 w-9 h-12 opacity-25 rotate-[20deg]" color="#F5C518" strokeWidth={1.6} />}
      {showBR && <Bolt className="absolute bottom-16 right-10 w-16 h-20 opacity-25 rotate-[-8deg]" color="#1E3FD8" strokeWidth={1.6} />}
      <SolidBolt className="absolute top-[30%] right-[5%] w-3 h-4 opacity-70 animate-flicker" color="#F5C518" />
      <SolidBolt className="absolute bottom-[35%] left-[3%] w-2 h-3 opacity-80" color="#1E3FD8" />
    </div>
  );
}

/**
 * Backwards-compatible wrapper — SectionBolts alias to SectionDecor.
 */
export function SectionBolts({ placement = "both" }) {
  return <SectionDecor variant="mixed" corners={placement} />;
}

/**
 * BoltDivider — horizontal divider line with a centered glowing bolt.
 */
export function BoltDivider() {
  return (
    <div className="flex items-center gap-4 my-4" aria-hidden="true">
      <span className="flex-1 h-px bg-gradient-to-r from-transparent via-voltage-blue/50 to-transparent" />
      <SolidBolt className="w-4 h-5 animate-pulse-glow" />
      <span className="flex-1 h-px bg-gradient-to-r from-transparent via-voltage-blue/50 to-transparent" />
    </div>
  );
}

/**
 * CornerBrackets — L-shaped corner brackets around a section heading area.
 * Use inside a `relative` container.
 */
export function CornerBrackets({ color = "#F5C518" }) {
  return (
    <>
      <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: color }} aria-hidden="true" />
      <span className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: color }} aria-hidden="true" />
      <span className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: color }} aria-hidden="true" />
      <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: color }} aria-hidden="true" />
    </>
  );
}
